import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconArrowLeft, IconArrowRight} from '@tabler/icons-react';
import React from 'react';
import {Button} from 'src/design/atoms/Button';
import {GeoStatusBanner} from 'src/app/inspections/geo/GeoStatusBanner';
import {SiteLocationMap} from 'src/app/inspections/geo/SiteLocationMap';
import {useGeoStatus} from 'src/app/inspections/geo/useGeoStatus';
import {useTransitionInspectionMutation} from 'src/redux/queries/inspection';
import {useAlert} from 'src/shared/hooks/useAlert';
import type {GeoCoordinates} from 'src/shared/utils/geoDistance';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface StartConfirmPanelProps {
  inspectionId: number;
  site?: GeoCoordinates;
  onBack: () => void;
}

/**
 * Confirm-location screen of the guided start flow. Geo never blocks Start; on success the
 * cache invalidation flips state to InProgress and the parent swaps screens — no navigation here.
 */
export const StartConfirmPanel: React.FC<StartConfirmPanelProps> = ({
  inspectionId,
  site,
  onBack,
}) => {
  const {setAlert} = useAlert();
  const [transitionInspection, {isLoading: isStarting}] =
    useTransitionInspectionMutation();
  const geo = useGeoStatus(true, site);

  const handleStart = async () => {
    try {
      await transitionInspection({
        id: inspectionId,
        request: {
          transition: 'start',
          latitude: geo.latitude,
          longitude: geo.longitude,
          geoNote: geo.geoNote,
        },
      }).unwrap();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <Stack gap="md">
      <Button
        variant="subtle"
        color="black"
        size="sm"
        px={4}
        leftSection={<IconArrowLeft size={16} />}
        onClick={onBack}
        style={{alignSelf: 'flex-start'}}>
        Overview
      </Button>

      <Stack gap={2}>
        <Text size="lg" fw={600}>
          Start inspection — confirm location
        </Text>
        <Text size="sm" c="dimmed">
          We use your device's location to confirm you're on site. This never
          blocks starting the inspection.
        </Text>
      </Stack>

      <GeoStatusBanner
        status={geo.status}
        distanceFeet={geo.distanceFeet}
        capturing={geo.isCapturing}
      />

      <SiteLocationMap
        site={site}
        user={
          geo.latitude !== undefined && geo.longitude !== undefined
            ? {latitude: geo.latitude, longitude: geo.longitude}
            : undefined
        }
        capturing={geo.isCapturing}
      />

      <Button
        fullWidth
        size="lg"
        color="yellow"
        loading={isStarting}
        rightSection={<IconArrowRight size={20} stroke={2} />}
        onClick={handleStart}>
        Start inspection
      </Button>
    </Stack>
  );
};
